import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SketchPicker } from 'react-color';
import { AdminInput } from 'shared-components';
import styles from './ColorPicker.module.css';

const PickerButton = ({ getColorPickerDisplay, toggle }) => (
  <button className={styles.pick} type="button" onClick={toggle}>
    {getColorPickerDisplay()}
  </button>
);

PickerButton.propTypes = {
  getColorPickerDisplay: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
};

const IconButton = ({ icon, action }) => (
  <button
    className={`naked-button ${styles.iconButton}`}
    type="button"
    onClick={action}
  >
    <i className={icon} />
  </button>
);

IconButton.propTypes = {
  action: PropTypes.func.isRequired,
  icon: PropTypes.string.isRequired,
};

const ColorPicker = ({
  value,
  error,
  label,
  name,
  handleColorChange,
  handleInputChange,
  openSecondColorPicker,
  firstColor,
  secondColor,
  resetField,
  displaySecondColorPicker,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((prevState) => !prevState);
  const containerRef = useRef(null);

  const handleClickOutside = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', handleClickOutside, false);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [isOpen]);

  const getColorPickerDisplay = () => {
    if (!value) {
      return <i className="fal fa-eye-dropper" />;
    }
    if (firstColor && secondColor) {
      return (
        <div
          className={styles.twoColors}
          style={{
            background: `linear-gradient(-45deg, ${secondColor}, ${secondColor} 49%, ${firstColor} 51%)`,
          }}
        />
      );
    }
    if (value) {
      return (
        <div className={styles.oneColor} style={{ backgroundColor: value }} />
      );
    }
  };

  const renderButton = () => {
    if (value && name === 'colorAttribute' && !secondColor) {
      return (
        <IconButton action={openSecondColorPicker} icon="far fa-plus-circle" />
      );
    }
    if (value) {
      return (
        <IconButton
          action={() => resetField(name)}
          icon="far fa-times-circle"
        />
      );
    }
  };

  return (
    <div className={styles.colorPickerContainer} ref={containerRef}>
      <div className={styles.colorInput}>
        <PickerButton
          getColorPickerDisplay={getColorPickerDisplay}
          toggle={toggle}
        />
        <AdminInput
          label={label}
          value={value || ''}
          type="text"
          onChange={handleInputChange}
          name={name}
          style={{ width: '158px' }}
          placeholder="Select Color"
          error={error}
        />
        {renderButton()}
      </div>
      {isOpen && (
        <div
          className={styles.colorPicker}
          onMouseLeave={() => setIsOpen(false)}
        >
          <SketchPicker
            onChange={(color) => handleColorChange(color, name)}
            color={value}
          />
        </div>
      )}
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};

ColorPicker.propTypes = {
  value: PropTypes.string,
  error: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  handleColorChange: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  openSecondColorPicker: PropTypes.func,
  firstColor: PropTypes.string,
  secondColor: PropTypes.string,
  resetField: PropTypes.func.isRequired,
  displaySecondColorPicker: PropTypes.bool,
};

ColorPicker.defaultProps = {
  value: '',
  error: null,
  openSecondColorPicker: null,
  firstColor: '',
  secondColor: '',
  displaySecondColorPicker: false,
};

export default ColorPicker;
